import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon, Input, SegmentedControl, Sheet } from '@astral/ui';
import {
  formatPackTitle,
  suggestShortNamePrefix,
  type PackBrandingPlacement,
} from '@astral/shared';
import { api, type PackNameCheck } from '../lib/api.js';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';

export interface PackLinkSheetProps {
  open: boolean;
  projectId: string;
  initialTitle: string;
  busy: boolean;
  /** Effective placement to preview and (when editable) to change. */
  branding: PackBrandingPlacement;
  /** Whether this account may choose — Premium/Premium Plus, ADMIN or OWNER. */
  canChooseBranding: boolean;
  onBrandingChange: (placement: PackBrandingPlacement) => void;
  /**
   * The placement travels WITH the confirm, not only through the editor's
   * debounced autosave. The selector lives in this sheet, so picking a tag and
   * tapping Publish immediately is the normal gesture — and it used to beat the
   * autosave to the server, which then published under the previous value.
   */
  onConfirm: (args: {
    shortName: string;
    title: string;
    branding?: PackBrandingPlacement;
  }) => void;
  onClose: () => void;
}

/**
 * First-publish step: choose the pack DISPLAY title (Astral brand previewed) and
 * the pack LINK prefix (the `_by_<bot>` suffix is fixed + shown, not editable).
 * Live-validates the final short name and checks availability before publishing.
 */
export function PackLinkSheet({
  open,
  projectId,
  initialTitle,
  busy,
  branding,
  canChooseBranding,
  onBrandingChange,
  onConfirm,
  onClose,
}: PackLinkSheetProps): JSX.Element {
  const t = useT();
  const [title, setTitle] = useState(initialTitle);
  const [prefix, setPrefix] = useState('');
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<PackNameCheck | null>(null);
  const seededRef = useRef(false);

  // Seed a suggested prefix from the title when the sheet first opens.
  useEffect(() => {
    if (open && !seededRef.current) {
      seededRef.current = true;
      setTitle(initialTitle);
      setPrefix(suggestShortNamePrefix(initialTitle));
    }
    if (!open) seededRef.current = false;
  }, [open, initialTitle]);

  // Debounced availability + format check against the backend.
  useEffect(() => {
    if (!open) return;
    const value = prefix.trim();
    if (value.length === 0) {
      setCheck(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    const handle = setTimeout(() => {
      void api
        .checkPackName(projectId, value)
        .then((result) => setCheck(result))
        .catch(() => setCheck(null))
        .finally(() => setChecking(false));
    }, 450);
    return () => clearTimeout(handle);
  }, [prefix, projectId, open]);

  const suffix = check?.suffix ?? '_by_…';
  const brandedTitle = useMemo(
    () => formatPackTitle(title.trim() || t('projectForm.titlePlaceholder'), branding),
    [title, branding, t],
  );

  const errorText = (): string | null => {
    if (checking || !check) return null;
    if (!check.valid) return t(`packLink.error.${check.error ?? 'chars'}` as TranslationKey);
    if (!check.available) return t('packLink.taken');
    return null;
  };
  const ready = !checking && Boolean(check?.valid) && Boolean(check?.available) && title.trim().length > 0;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('packLink.title')}
      footer={
        <Button
          fullWidth
          size="lg"
          loading={busy}
          disabled={!ready || busy}
          leftIcon={<Icon name="sparkles" size={18} />}
          onClick={() =>
            onConfirm({
              shortName: prefix.trim(),
              title: title.trim(),
              // Only an account that may actually choose states a placement;
              // for everyone else the server's own resolution is the answer.
              ...(canChooseBranding ? { branding } : {}),
            })
          }
        >
          {t('publish.action')}
        </Button>
      }
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="stack" style={{ gap: 6 }}>
          <Input
            label={t('packLink.nameLabel')}
            value={title}
            maxLength={48}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="packlink-preview">{brandedTitle}</div>
          {/*
            Only eligible accounts get the selector. Free users see the preview
            (always tagged) with no control — the placement is not theirs to
            change, and the server enforces that regardless of what is sent.
          */}
          {canChooseBranding && (
            <div className="stack" style={{ gap: 6, marginTop: 6 }}>
              <span className="muted" style={{ fontSize: 12 }}>{t('packLink.branding.label')}</span>
              <SegmentedControl<PackBrandingPlacement>
                value={branding}
                onChange={onBrandingChange}
                options={[
                  { value: 'NONE', label: t('packLink.branding.none') },
                  { value: 'START', label: t('packLink.branding.start') },
                  { value: 'END', label: t('packLink.branding.end') },
                ]}
              />
            </div>
          )}
        </div>

        <div className="stack" style={{ gap: 6 }}>
          <span className="as-field__label">{t('packLink.linkLabel')}</span>
          <div className="packlink-field">
            <span className="packlink-field__base">t.me/addstickers/</span>
            <input
              className="packlink-field__input"
              value={prefix}
              inputMode="text"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t('packLink.linkPlaceholder')}
              onChange={(e) => setPrefix(e.target.value)}
            />
          </div>
          <div className="packlink-suffix">
            <span className="muted">{suffix}</span>
            {checking ? (
              <span className="muted">{t('packLink.checking')}</span>
            ) : check?.valid && check.available ? (
              <span className="packlink-ok">
                <Icon name="check" size={14} /> {t('packLink.available')}
              </span>
            ) : errorText() ? (
              <span className="packlink-bad">{errorText()}</span>
            ) : null}
          </div>
          {check?.valid && check.url && (
            <div className="packlink-url">{check.url}</div>
          )}
        </div>
      </div>
    </Sheet>
  );
}
